import { Box, Typography } from "@mui/material";
import { GeneratorStatusReviewExplanationBasis, ResolutionStatus } from "generated-graphql/graphql";
import { DateTime } from "luxon";

import { reviewLifecycle, ReviewEvent } from "./reviewHistory";

type Props = {
  status: ResolutionStatus;
  validatedAt?: string | null;
  events?: readonly ReviewEvent[];
  hasLinkedSubmission: boolean;
  hasCorrections: boolean;
};

type ReviewSummary = {
  title: string;
  description: string;
  note?: string | null;
  basis?: NonNullable<ReviewEvent["explanation"]>["basis"];
  comparison?: string | null;
};

function eventDate(event: ReviewEvent) {
  return DateTime.fromISO(event.createdAt, { zone: "utc" })
    .toLocal()
    .toLocaleString(DateTime.DATE_MED);
}

function eventSentence(event: ReviewEvent, action: string, outcome = "") {
  const actor = event.actorId === "system" ? "The system" : event.actorName;
  const suffix = outcome ? ` ${outcome}` : "";
  return actor
    ? `${actor} ${action} this review${suffix} on ${eventDate(event)}.`
    : `This review was ${action}${suffix} on ${eventDate(event)}.`;
}

function reopeningDescription(event: ReviewEvent, previous?: ReviewEvent) {
  if (event.explanation) return event.explanation.message;
  if (event.actorId === "system" && previous?.eventType === "AUTO_RESOLVED") {
    return `The system reopened this review on ${eventDate(event)} after automatically marking it resolved on ${eventDate(previous)}.`;
  }
  return eventSentence(event, "reopened");
}

function followUpDescription({ hasLinkedSubmission, hasCorrections }: Props) {
  if (hasLinkedSubmission && hasCorrections) {
    return "This review is awaiting follow-up on the linked submission and manifest corrections.";
  }
  if (hasLinkedSubmission) return "This review is awaiting completion of the linked submission.";
  if (hasCorrections) return "This review is awaiting the linked manifest corrections.";
  return "This review has been marked as awaiting follow-up.";
}

function resolvedReviewSummary(latest?: ReviewEvent) {
  const automatic = latest?.eventType === "AUTO_RESOLVED";
  const event = automatic || latest?.eventType === "RESOLVED" ? latest : undefined;
  return {
    title: automatic ? "Automatically marked resolved" : "Marked resolved",
    description: event
      ? eventSentence(event, "marked", "resolved")
      : "This review has been marked resolved.",
    note: event?.note,
  };
}

function reviewSummary(props: Props): ReviewSummary {
  const lifecycle = reviewLifecycle(props.events ?? []);
  const latest = lifecycle.at(-1);
  if (props.status === ResolutionStatus.Pending) {
    return { title: "Awaiting follow-up", description: followUpDescription(props) };
  }
  if (props.status === ResolutionStatus.Ignored) {
    const event = latest?.eventType === "IGNORED" ? latest : undefined;
    return {
      title: "Dismissed",
      description: event ? eventSentence(event, "dismissed") : "This review has been dismissed.",
      note: event?.note,
    };
  }
  if (props.status === ResolutionStatus.Resolved) {
    return resolvedReviewSummary(latest);
  }
  const reopened = latest?.eventType === "REOPENED";
  const title = props.validatedAt
    ? "Ready for follow-up"
    : reopened
      ? "Reopened for review"
      : "Awaiting validation";
  if (reopened) {
    return {
      title,
      description: reopeningDescription(latest, lifecycle.at(-2)),
      basis: latest.explanation?.basis,
      note:
        latest.explanation?.basis === GeneratorStatusReviewExplanationBasis.RecordedReason
          ? undefined
          : latest.note,
      comparison: latest.comparison,
    };
  }
  return {
    title,
    description: props.validatedAt
      ? "This discrepancy has been validated and is ready for follow-up."
      : latest?.eventType === "OPENED"
        ? eventSentence(latest, "opened")
        : "Review the reported and calculated statuses to validate or dismiss this discrepancy.",
  };
}

export function ReviewStatusSummary(props: Props) {
  const summary = reviewSummary(props);
  return (
    <Box>
      <Typography variant="body2" fontWeight={600}>
        {summary.title}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {summary.basis === GeneratorStatusReviewExplanationBasis.HistoricalComparison &&
          "Recorded comparisons: "}
        {summary.basis === GeneratorStatusReviewExplanationBasis.RecordedReason &&
          "Recorded reason: "}
        {summary.description}
      </Typography>
      {summary.comparison && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {summary.comparison}
        </Typography>
      )}
      {summary.note && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Recorded reason: {summary.note}
        </Typography>
      )}
    </Box>
  );
}
